import React, { useState, useEffect, useContext } from 'react'
import { api, formatCurrency, formatDate } from '../../utils/api'
import { AuthContext } from '../../hooks/useAuth.jsx'
import { DEAL_STAGES, getStageName } from '../../utils/dealConstants'
import { displayCondition } from '../warehouse/conditionDisplay'
import ImageQualityTable from './ImageQualityTable'
import { parsePricingLog } from '../../utils/pricingLogParser'
import { isAwaitingPricing, isPricedButStale } from './pricingStatus'
import { isParkedForReview, parkReasonLabel } from './quote-assessment/parkedForReview'
import AddItemDialog from './quote-assessment/AddItemDialog.jsx'
import ConfirmActionDialog from './quote-assessment/ConfirmActionDialog.jsx'
import DealLink from '../shared/DealLink.jsx'
import LinePrices from './LinePrices.jsx'
import './quote-assessment/quote-assessment.css'

const ITEM_ERROR_TOAST_MS = 6000

function getValidationBadge(log) {
  if (!log) return null
  if (log.includes('⚠ MODEL VALIDATION WARNING') || log.includes('⚠ LOW IDENTITY CONFIDENCE')) {
    return { className: 'validation-badge warning', label: '⚠ Validation Warning' }
  }
  if (log.includes('--- BLOCKED ---')) {
    return { className: 'validation-badge blocked', label: 'Blocked (legacy)' }
  }
  return { className: 'validation-badge passed', label: '✓ Validated' }
}

// Parse Claude pricing log into structured sections
function parsePricingLogSections(log) {
  if (!log) return []

  const sectionDefs = [
    { marker: /^\[\d{4}-\d{2}-\d{2}/, title: 'CLAUDE PRICING LOG' },
    { marker: /^Status:\s*CONFIRMED|^Visible Text:|^---\s*MODEL VALIDATION\s*---/m, title: 'MODEL VALIDATION' },
    { marker: /^IC:\s|^---\s*CONFIDENCE SCORES\s*---/m, title: 'CONFIDENCE SCORES' },
    { marker: /^Score:\s*\d+\s*\(|^---\s*DATASET CONFIDENCE SCORE\s*---/m, title: 'DATASET CONFIDENCE SCORE' },
    { marker: /^Total filtered:|^---\s*CONDITION SEGMENTATION\s*---/m, title: 'CONDITION SEGMENTATION' },
    { marker: /^Method:\s*(AI|Rule)|^---\s*SEGMENT CLASSIFICATION\s*---/m, title: 'SEGMENT CLASSIFICATION' },
    { marker: /^Text SKUs:|^---\s*RECONCILIATION\s*---/m, title: 'RECONCILIATION' },
    { marker: /^---\s*SMALL SAMPLE|^Cleaned sold|^---\s*PRICE BUCKETING ANALYSIS\s*---/m, title: 'PRICE BUCKETING ANALYSIS' },
    { marker: /^Intake condition:|^Baseline price|^---\s*CONDITION ADJUSTMENT\s*---/m, title: 'CONDITION ADJUSTMENT' },
    { marker: /^Sold Prices \(shipped\):|^Active BIN:|^---\s*EBAY UK SOLD/m, title: 'EBAY UK SOLD' },
    { marker: /^Used\/eBay:|^---\s*GOOGLE SHOPPING/m, title: 'GOOGLE SHOPPING' },
    { marker: /^Amazon:|^---\s*AMAZON UK/m, title: 'AMAZON UK' },
    { marker: /^Sold:.*Active:|^Collection-only \(local|^---\s*REVERB UK/m, title: 'REVERB UK' },
    { marker: /^Intake brand:|^Model compare:|^---\s*MODEL VALIDATION GATE\s*---/m, title: 'MODEL VALIDATION GATE' },
    { marker: /^R2 lookup:|^Found \d+ images|^\[AZURE-OCR\]|^---\s*IMAGE DEBUG\s*---/m, title: 'IMAGE DEBUG' },
  ]

  const lines = log.split('\n')
  const sections = []
  let currentTitle = 'CLAUDE PRICING LOG'
  let currentLines = []

  for (const line of lines) {
    // Skip existing --- SECTION --- markers (we'll add our own headers)
    if (/^---\s*[A-Z].*---\s*$/.test(line.trim())) {
      // Check if this line itself triggers a new section
      let matched = false
      for (const def of sectionDefs) {
        if (def.title !== currentTitle && def.marker.test(line)) {
          if (currentLines.length > 0) {
            sections.push({ title: currentTitle, content: currentLines.join('\n').trim() })
          }
          currentTitle = def.title
          currentLines = []
          matched = true
          break
        }
      }
      if (!matched) continue // skip standalone --- markers
      continue
    }

    // Skip trailing bare "---" lines
    if (line.trim() === '---') continue

    let matched = false
    for (const def of sectionDefs) {
      if (def.title !== currentTitle && def.marker.test(line)) {
        if (currentLines.length > 0) {
          sections.push({ title: currentTitle, content: currentLines.join('\n').trim() })
        }
        currentTitle = def.title
        currentLines = [line]
        matched = true
        break
      }
    }
    if (!matched) {
      currentLines.push(line)
    }
  }
  if (currentLines.length > 0) {
    sections.push({ title: currentTitle, content: currentLines.join('\n').trim() })
  }

  // Merge duplicate titles
  const merged = []
  const seen = new Set()
  for (const sec of sections) {
    if (seen.has(sec.title)) {
      const existing = merged.find(m => m.title === sec.title)
      if (existing) existing.content += '\n' + sec.content
    } else {
      seen.add(sec.title)
      merged.push({ ...sec })
    }
  }

  return merged.filter(s => s.content)
}

function QuoteRequested() {
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  // Customer dropdown state
  const [expandedDeal, setExpandedDeal] = useState(null)
  const [customerDeals, setCustomerDeals] = useState({})
  const [loadingCustomerDeals, setLoadingCustomerDeals] = useState({})

  // Deal images (R2)
  const [dealImages, setDealImages] = useState({})
  const [loadingDealImages, setLoadingDealImages] = useState(null)
  const [expandedDealImages, setExpandedDealImages] = useState(null)

  // Lightbox state
  const [lightbox, setLightbox] = useState({ open: false, images: [], currentIndex: 0 })

  // Other deal details popup
  const [selectedOtherDeal, setSelectedOtherDeal] = useState(null)
  const [updatingOtherDealStage, setUpdatingOtherDealStage] = useState(false)
  const [markingOtherDuplicate, setMarkingOtherDuplicate] = useState(false)

  // Pricing log viewing/editing
  const [pricingLogView, setPricingLogView] = useState({}) // { [dealId]: 'log' | null }
  const [editingPricingLog, setEditingPricingLog] = useState(null) // dealId
  const [pricingLogValue, setPricingLogValue] = useState('')
  const [savingPricingLog, setSavingPricingLog] = useState(false)
  const [repricingDeal, setRepricingDeal] = useState(null)
  const [repriceToast, setRepriceToast] = useState(null)

  // Item add / remove: admins only. Everyone else sees the read-only list.
  const isAdmin = !!useContext(AuthContext)?.user?.is_admin
  const [addItemDeal, setAddItemDeal] = useState(null) // dealId
  const [removeAsk, setRemoveAsk] = useState(null) // { deal, quoteItemId, position, name }
  const [savingItem, setSavingItem] = useState(false)

  function openLightbox(images, startIndex = 0) {
    setLightbox({ open: true, images, currentIndex: startIndex })
  }

  function closeLightbox() {
    setLightbox({ open: false, images: [], currentIndex: 0 })
  }

  function nextImage() {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length
    }))
  }

  function prevImage() {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
    }))
  }

  // Handle keyboard navigation in lightbox
  useEffect(() => {
    if (!lightbox.open) return

    function handleKeyDown(e) {
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowRight') nextImage()
      if (e.key === 'ArrowLeft') prevImage()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightbox.open])

  useEffect(() => {
    loadDeals()
  }, [])

  async function loadDeals() {
    setLoading(true)
    try {
      const response = await api.get('/api/deals')
      setDeals(response.deals || [])
    } catch (error) {
      console.error('Failed to load deals:', error)
    } finally {
      setLoading(false)
    }
  }

  // Parse items from parsed_items string
  function parseItems(itemsString) {
    if (!itemsString) return []
    return itemsString.split('\n').filter(line => line.trim())
  }

  // Extract score from claude_pricing_log (fallback for single-item deals)
  function getScoreFromLog(log) {
    if (!log) return null
    const match = log.match(/Score:\s*(\d+)/)
    return match ? parseInt(match[1]) : null
  }

  // Parse a single item line into components
  // Handles both old format: "1. Name - Details - Qty:1 - SP:£709 - BP:£351"
  // and new format: "Name, condition, qty 6"
  function parseItemLine(line) {
    const withoutNumber = line.replace(/^\d+\.\s*/, '')
    // Belt-and-braces: the product name (before the first " - ") can contain
    // tokens like "BP150" that look like BP tags. Only scan the details
    // portion for price tags, and require the "SP:"/"BP:" colon format.
    const firstDashIdx = withoutNumber.indexOf(' - ')
    const detailsPortion = firstDashIdx >= 0 ? withoutNumber.substring(firstDashIdx) : ''
    const spMatch = detailsPortion.match(/\bSP:\s*£?([\d.]+)/i)
    const bpMatch = detailsPortion.match(/\bBP:\s*£?([\d.]+)/i)
    const qtyMatch = detailsPortion.match(/\bQty[:\s]\s*(\d+)/i)
    const scoreMatch = detailsPortion.match(/\bSc:(\d+)/i)
    // New format carries a price: " - AI:£1512". Legacy format was a bare
    // " - AI" source flag. Capture the price when present.
    const aiMatch = detailsPortion.match(/\s-\sAI(?::\s*£?([\d.]+))?/i)

    let description = withoutNumber
      .replace(/\s*-\s*Qty[:\s]\s*\d+/gi, '')
      .replace(/,\s*qty\s+\d+/i, '')
      .replace(/\s*-?\s*\bSP:\s*£?[\d.]+/i, '')
      .replace(/\s*-?\s*\bBP:\s*£?[\d.]+/i, '')
      .replace(/\s*-\s*Sc:\d+/i, '')
      .replace(/\s*-\s*AI(?::\s*£?[\d.]+)?/i, '')
      .replace(/\s*\(PENDING\)/i, '')
      .replace(/\s*\[LOW CONFIDENCE\]/gi, '')
      .trim()

    // Extract condition from " - condition" format (new) or ", condition" format (legacy)
    const knownConditions = ['mint', 'excellent', 'very good', 'good', 'fair', 'poor', 'broken']
    let condition = null
    const lastDash = description.lastIndexOf(' - ')
    if (lastDash > 0) {
      const afterDash = description.substring(lastDash + 3).trim().toLowerCase()
      if (knownConditions.includes(afterDash)) {
        condition = description.substring(lastDash + 3).trim()
        description = description.substring(0, lastDash).trim()
      }
    }
    if (!condition) {
      const descParts = description.split(',').map(p => p.trim())
      if (descParts.length >= 2) {
        const lastPart = descParts[descParts.length - 1].toLowerCase()
        if (knownConditions.includes(lastPart)) {
          condition = descParts.pop().trim()
          description = descParts.join(', ').trim()
        }
      }
    }

    return {
      description,
      condition,
      qty: qtyMatch ? qtyMatch[1] : '1',
      sp: spMatch ? spMatch[1] : '0',
      bp: bpMatch ? bpMatch[1] : '0',
      score: scoreMatch ? parseInt(scoreMatch[1]) : null,
      aiPriceSource: !!aiMatch,
      aiPrice: aiMatch && aiMatch[1] ? aiMatch[1] : null
    }
  }

  // Save pricing log edit (writes to D1 via dedicated endpoint)
  async function savePricingLog(dealId) {
    setSavingPricingLog(true)
    try {
      const result = await api.put(`/api/deals/${dealId}/pricing-log`, { claude_pricing_log: pricingLogValue })
      setDeals(deals.map(d => d.id === dealId ? { ...d, claude_pricing_log: pricingLogValue, pricing_log_status: result.status } : d))
      setEditingPricingLog(null)
    } catch (error) {
      alert(`Error saving pricing log: ${error.message}`)
    } finally {
      setSavingPricingLog(false)
    }
  }

  // Force-reprice via the pricing worker. This used to clear claude_pricing_log in D1,
  // which stopped working at the Prism cutover: eligibility is now "does Prism offer this
  // quote in G1", and G1 filters on the items having no price tiers. Clearing our row
  // changed none of that, so the button reported success and nothing repriced.
  // Re-pricing clears EVERY tier on the quote (Prism's G4 verb is "clear every live item's
  // tiers"), so a partially-priced quote loses the items that already succeeded. The list
  // cannot currently tell partial from unstarted — both read "Awaiting pricing" — so the
  // operator has no way to see what they are about to discard. Until the deals API returns
  // priced/total counts, say it in the confirm rather than let good work vanish silently.
  const REPRICE_CONFIRM = [
    'Re-price this quote?',
    '',
    'This clears EVERY price already on the quote and prices all its items again from scratch.',
    'If some items were already priced, that work is discarded and re-run — and the list',
    'cannot currently show you which ones.',
  ].join('\n')

  async function forceReprice(dealId, quoteId) {
    if (!confirm(REPRICE_CONFIRM)) return
    setRepricingDeal(dealId)
    try {
      // quote_id is a hint: it saves the server an A5 search for a mirror-era
      // quote. The server resolves regardless, so a row without one still works.
      const result = await api.post(`/api/deals/${dealId}/requeue`, { quote_id: quoteId })
      // Report the worker's own verdict. It does three things — clears the tiers, clears
      // the Prism exclusion, resets D1 — and a partial success must not read as done.
      if (result?.ok === false) {
        setRepriceToast({ message: result.error || 'Re-queue failed', type: 'error' })
        setTimeout(() => setRepriceToast(null), 6000)
        return
      }
      setDeals(deals.map(d => d.id === dealId ? { ...d, claude_pricing_log: '', pricing_log_status: 'pending' } : d))
      setPricingLogView(prev => ({ ...prev, [dealId]: null }))
      setRepriceToast({ message: 'Re-queued for pricing', type: 'success' })
      setTimeout(() => setRepriceToast(null), 3000)
    } catch (error) {
      setRepriceToast({ message: `Error: ${error.message}`, type: 'error' })
      setTimeout(() => setRepriceToast(null), 4000)
    } finally {
      setRepricingDeal(null)
    }
  }

  // Add or remove an item (admins). Both routes answer with the item list
  // rebuilt from what Prism stored, and that reply replaces the row's items.
  // A removed item is rejected in Prism, not deleted: it leaves the quote and
  // its total. An added item lands unpriced.
  async function writeItem(dealId, write) {
    setSavingItem(true)
    try {
      const r = await write()
      setDeals(prev => prev.map(d => d.id === dealId
        ? { ...d, parsed_items: r.parsedItems, condition: r.condition ?? null, items: r.items ?? [] }
        : d))
    } catch (error) {
      setRepriceToast({ message: `Error: ${error.message}`, type: 'error' })
      setTimeout(() => setRepriceToast(null), ITEM_ERROR_TOAST_MS)
    } finally {
      setSavingItem(false)
    }
  }

  function addItem(fields) {
    const dealId = addItemDeal
    setAddItemDeal(null)
    return writeItem(dealId, () => api.post(`/api/deals/${dealId}/items`, fields))
  }

  function removeItem() {
    const { deal, quoteItemId } = removeAsk
    setRemoveAsk(null)
    return writeItem(deal.id, () => api.delete(`/api/deals/${deal.id}/items/${quoteItemId}`))
  }

  // Reconcile a D1 'pending' row with a quote Prism has already fully priced (see
  // isPricedButStale). The server re-checks Prism's item state before flipping the row;
  // the prices stay — they are the engine's own, so this is a label fix, not a reprice.
  async function markPriced(dealId, quoteId) {
    setRepricingDeal(dealId)
    try {
      const result = await api.post(`/api/deals/${dealId}/mark-priced`, { quote_id: quoteId })
      if (result?.ok === false) {
        setRepriceToast({ message: result.error || 'Mark priced failed', type: 'error' })
        setTimeout(() => setRepriceToast(null), 6000)
        return
      }
      setDeals(deals.map(d => d.id === dealId ? { ...d, pricing_log_status: 'completed' } : d))
      setRepriceToast({ message: 'Marked as priced', type: 'success' })
      setTimeout(() => setRepriceToast(null), 3000)
    } catch (error) {
      setRepriceToast({ message: `Error: ${error.message}`, type: 'error' })
      setTimeout(() => setRepriceToast(null), 6000)
    } finally {
      setRepricingDeal(null)
    }
  }

  // Update other deal stage from popup
  async function updateOtherDealStage(dealId, newStage, parentDealId) {
    setUpdatingOtherDealStage(true)
    try {
      await api.patch(`/api/deals/${dealId}/status`, { dealstage: newStage })
      // Update in customerDeals cache
      setCustomerDeals(prev => ({
        ...prev,
        [parentDealId]: prev[parentDealId]?.map(d =>
          d.id === dealId ? { ...d, dealstage: newStage, stageName: getStageName(newStage) } : d
        )
      }))
      setSelectedOtherDeal(prev => prev ? { ...prev, dealstage: newStage } : null)
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setUpdatingOtherDealStage(false)
    }
  }

  // Mark other deal as duplicate from popup
  async function markOtherDealAsDuplicate(dealId, parentDealId) {
    if (!confirm('Mark this deal as duplicate?')) return
    setMarkingOtherDuplicate(true)
    try {
      await api.patch(`/api/deals/${dealId}/status`, { dealstage: '1616888039' })
      // Remove from customerDeals cache
      setCustomerDeals(prev => ({
        ...prev,
        [parentDealId]: prev[parentDealId]?.filter(d => d.id !== dealId)
      }))
      setSelectedOtherDeal(null)
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setMarkingOtherDuplicate(false)
    }
  }

  // Toggle customer dropdown
  async function toggleExpanded(dealId, email) {
    if (expandedDeal === dealId) {
      setExpandedDeal(null)
    } else {
      setExpandedDeal(dealId)
      if (!customerDeals[dealId] && email) {
        setLoadingCustomerDeals(prev => ({ ...prev, [dealId]: true }))
        try {
          const result = await api.get(`/api/deals/customer-deals?email=${encodeURIComponent(email)}`)
          setCustomerDeals(prev => ({ ...prev, [dealId]: result.deals || [] }))
        } catch (error) {
          console.error('Error:', error)
        } finally {
          setLoadingCustomerDeals(prev => ({ ...prev, [dealId]: false }))
        }
      }
    }
  }

  // Toggle deal images
  async function toggleDealImages(dealId) {
    if (expandedDealImages === dealId) {
      setExpandedDealImages(null)
      return
    }
    setExpandedDealImages(dealId)
    if (dealImages[dealId]) return

    setLoadingDealImages(dealId)
    try {
      const result = await api.get(`/api/deals/${dealId}/images`)
      setDealImages(prev => ({ ...prev, [dealId]: result.images || [] }))
    } catch (error) {
      console.error('Error:', error)
      setDealImages(prev => ({ ...prev, [dealId]: [] }))
    } finally {
      setLoadingDealImages(null)
    }
  }

  // Download the photo currently shown in the lightbox. Fetches the blob so
  // the download works for cross-origin R2 URLs; falls back to opening the
  // image in a new tab if the fetch is blocked.
  async function downloadCurrentImage() {
    const url = lightbox.images[lightbox.currentIndex]
    if (!url) return
    const filename = (url.split('?')[0].split('/').pop()) || `deal-image-${lightbox.currentIndex + 1}.jpg`
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(objectUrl)
    } catch (err) {
      console.error('Image download failed, opening in new tab:', err)
      window.open(url, '_blank', 'noopener')
    }
  }

  // Download a deal's bulk inventory-file attachment (xlsx/csv deals).
  async function downloadInventoryFile(dealId) {
    try {
      const res = await fetch(`/api/deals/${dealId}/inventory-file`, { credentials: 'include' })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Download failed' }))
        throw new Error(err.error || 'Download failed')
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const disposition = res.headers.get('content-disposition') || ''
      const match = disposition.match(/filename="(.+)"/)
      a.download = match ? match[1] : 'inventory-file.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      alert(`Download failed: ${err.message}`)
    }
  }

  // Filter deals by search term
  const filteredDeals = deals.filter(deal => {
    const searchLower = searchTerm.toLowerCase()
    return (
      (deal.dealname || '').toLowerCase().includes(searchLower) ||
      (deal.id || '').includes(searchLower) ||
      (deal.parsed_items || '').toLowerCase().includes(searchLower) ||
      (deal.seller_email || '').toLowerCase().includes(searchLower)
    )
  })

  // Pricing status badge for the deal row, from the D1 status enriched onto each
  // deal. Returns null for a clean priced deal.
  //
  // The final "Awaiting pricing" case defers to isAwaitingPricing, which the tile also
  // counts — the two used to disagree because the tile tested a different field. The
  // ternary looks redundant today (anything reaching it is awaiting by construction) and
  // is not: adding a status to NOT_AWAITING then correctly drops the badge here AND the
  // count there, instead of silently splitting them apart again.
  // D1 'pending' on a quote Prism has fully priced: nothing will ever pick it up again,
  // so it must not read as "Awaiting pricing" — not on the row, and not in the tile.
  const pricedButStale = (deal) => isPricedButStale(deal, parseItems(deal.parsed_items).map(parseItemLine))

  const pricingStatusBadge = (deal) => {
    const status = deal.pricing_log_status
    const noItems = parseItems(deal.parsed_items).length === 0
    if (pricedButStale(deal)) return { label: 'Priced — status stale', cls: 'badge-warning' }
    // Engine parks reach this page now (the flag-wide filter is gone) — say WHY
    // rather than a generic failure. Customer-waiting parks never get here.
    if (isParkedForReview(deal)) {
      return { label: `Parked — ${parkReasonLabel(deal.excluded_reason)}`, cls: 'badge-error' }
    }
    if (status === 'error') {
      if (deal.vision_failure_cause === 'REFUSAL') return { label: 'Vision refused', cls: 'badge-error' }
      return { label: 'Pricing failed', cls: 'badge-error' }
    }
    if (status === 'retrying') {
      return { label: deal.retry_count ? `Retrying (attempt ${deal.retry_count})` : 'Retrying…', cls: 'badge-warning' }
    }
    if (status === 'completed') {
      // Completed but produced no item = identified-but-unpriceable (NEEDS_REVIEW / £0).
      if (noItems) return { label: 'No price', cls: 'badge-warning' }
      return deal.manual_review_flag ? { label: 'Needs review', cls: 'badge-warning' } : null
    }
    return isAwaitingPricing(deal) ? { label: 'Awaiting pricing', cls: 'badge-neutral' } : null
  }

  // Best explanatory line from the pricing log, skipping the timestamp/"Items:" noise.
  const pricingFailureReason = (log) => {
    if (!log) return null
    const lines = log.split('\n').map(l => l.trim()).filter(Boolean)
    const hit = lines.find(l => /(identification failed|could not|insufficient|no usable|no[_ ]sold|no market|contamination|skipped|needs[ _]review)/i.test(l))
    if (hit) return hit.replace(/^Identification failed:\s*/i, '').replace(/^Notes:\s*/i, '').replace(/\s*\|.*$/, '')
    return lines.find(l => !/^\[?\d{4}-\d{2}-\d{2}/.test(l) && !/^items:?$/i.test(l)) || null
  }

  // Render parsed items inline. Read-only for most users: editing and per-item
  // pricing live on Quote Assessment, and this list displays what the pricing
  // engine produced. Admins also get add and remove here, for quotes that never
  // reach Quote Assessment (failed pricing).
  function renderParsedItems(deal) {
    const items = parseItems(deal.parsed_items)
    const status = deal.pricing_log_status
    // Show the panel whenever a deal has no priced items but WAS processed — this
    // covers hard failures (error/retrying) AND completed-but-unpriceable
    // (NEEDS_REVIEW / £0 / no market data), which otherwise render blank.
    const showPanel = items.length === 0 && (!!deal.claude_pricing_log || !!status)
    const reason = pricingFailureReason(deal.claude_pricing_log)
    const headline = status === 'error' ? '⚠ Pricing failed'
      : status === 'retrying' ? '⟳ Pricing retrying…'
      : status === 'completed' ? 'ⓘ No usable price — needs review'
      : 'ⓘ Awaiting pricing'
    const panelKind = status === 'error' ? 'error' : status === 'retrying' ? 'retrying' : 'review'
    // Retry only makes sense once pricing has actually run (failed / retrying /
    // produced no usable price). A deal still queued for its first attempt has
    // nothing to retry — showing the button there just reads as an error.
    const attempted = status === 'error' || status === 'retrying' || status === 'completed'

    return (
      <div className="parsed-items-list">
        {showPanel && (
          <div className={`pricing-failure ${panelKind}`}>
            <div className="pricing-failure-headline">
              {headline}
              {reason && <span className="pricing-failure-reason"> — {reason}</span>}
            </div>
            {deal.vision_failure_cause === 'REFUSAL' && (
              <div className="pricing-failure-hint">
                Vision declined to read the photos — usually a person in frame. Won't retry automatically; price manually or re-shoot without people.
              </div>
            )}
            {attempted && (
              <div className="pricing-failure-actions">
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => forceReprice(deal.id, deal.quote_id)}
                  disabled={repricingDeal === deal.id}
                  title="Clear ALL prices on this quote and price every item again from scratch (~2 min)"
                >
                  {repricingDeal === deal.id ? 'Re-queuing…' : '↻ Retry pricing'}
                </button>
              </div>
            )}
            {deal.claude_pricing_log && (
              <details className="pricing-failure-log">
                <summary>View pricing log</summary>
                <pre>{deal.claude_pricing_log}</pre>
              </details>
            )}
          </div>
        )}
        {items.map((item, index) => {
          const parsed = parseItemLine(item)
          const effectiveCondition = parsed.condition || deal.condition || null
          // Use parsed score if available, otherwise fall back to log score for single-item deals
          const score = parsed.score !== null ? parsed.score : (items.length === 1 ? getScoreFromLog(deal.claude_pricing_log) : null)
          // `deal.items` pairs with the lines by position; the id is what the
          // write addresses. A line with no id gets no remove button.
          const quoteItemId = deal.items?.[index]?.quote_item_id ?? null

          return (
            <div key={index} className="parsed-item-card">
              <div className="parsed-item-main">
                <span className="parsed-item-number">{index + 1}</span>
                {score !== null && (
                  <span className={`score-badge ${score >= 150 ? 'high' : score >= 100 ? 'medium' : score >= 45 ? 'low' : 'very-low'}`}>
                    {score}
                  </span>
                )}
                {parsed.aiPriceSource && (
                  <span className="badge badge-sm badge-info" title="AI-grounded recommended sell price (cross-check only)">
                    {parsed.aiPrice ? `AI £${parsed.aiPrice}` : 'AI'}
                  </span>
                )}
                <span className={`badge badge-sm ${effectiveCondition ? getConditionClass(effectiveCondition) : 'badge-neutral'}`}>
                  {effectiveCondition ? displayCondition(effectiveCondition) : 'cond'}
                </span>
                <span className="parsed-item-description">{parsed.description}</span>
              </div>
              <div className="parsed-item-prices">
                <LinePrices qty={parsed.qty} sp={parsed.sp} bp={parsed.bp} />
                {isAdmin && quoteItemId && (
                  <button
                    className="search-btn delete-btn"
                    title="Remove item"
                    aria-label={`Remove item ${index + 1}`}
                    disabled={savingItem}
                    onClick={() => setRemoveAsk({ deal, quoteItemId, position: index + 1, name: parsed.description })}
                  >×</button>
                )}
              </div>
            </div>
          )
        })}
        {isAdmin && (
          <button className="btn btn-sm btn-secondary" disabled={savingItem} onClick={() => setAddItemDeal(deal.id)}>
            + Add item
          </button>
        )}
      </div>
    )
  }

  // Deal-level pricing tools (the old per-item ▼ dropdown, promoted to the row).
  // Toggles for the wide panels (images, log) live here; the panels themselves
  // render in a full-width detail row so they aren't crushed into a column.
  function renderTools(deal) {
    const hasLog = !!(deal.claude_pricing_log || deal.pricing_log_status)
    const isBulkFile = deal.dealname && /\.xlsx?|\.csv/i.test(deal.dealname)
    // 'pending' normally means a run is queued, so Force Re-price is disabled. Stale
    // 'pending' (every item already priced) is the one case nothing is coming: both ways
    // out are offered — keep the prices (Mark priced) or start over (Force Re-price).
    const stale = pricedButStale(deal)
    const queued = deal.pricing_log_status === 'pending' && !stale
    return (
      <div className="deal-tools">
        <button
          className={`btn btn-xs ${expandedDealImages === deal.id ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => toggleDealImages(deal.id)}
        >
          {expandedDealImages === deal.id ? 'Hide Images' : 'View Images'}
        </button>
        {hasLog && (
          <button
            className={`btn btn-xs ${pricingLogView[deal.id] === 'log' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setPricingLogView(prev => ({ ...prev, [deal.id]: prev[deal.id] === 'log' ? null : 'log' }))
              setEditingPricingLog(null)
            }}
          >
            {pricingLogView[deal.id] === 'log' ? 'Hide Log' : 'Show Log'}
          </button>
        )}
        <button
          className="btn btn-xs"
          style={{ background: '#7f1d1d', color: '#fca5a5', borderColor: '#991b1b' }}
          onClick={() => forceReprice(deal.id, deal.quote_id)}
          disabled={repricingDeal === deal.id || queued}
        >
          {repricingDeal === deal.id ? 'Re-pricing...' : queued ? 'Already queued' : 'Force Re-price'}
        </button>
        {stale && (
          <button
            className="btn btn-xs btn-primary"
            onClick={() => markPriced(deal.id, deal.quote_id)}
            disabled={repricingDeal === deal.id}
            title="Every item already has a price in Prism but the pricing status is stuck on pending. Keep the prices and mark the quote as priced."
          >
            Mark priced
          </button>
        )}
        <ImageQualityTable dealId={deal.id} />
        {deal.collection_link && (
          <a href={deal.collection_link} target="_blank" rel="noopener noreferrer" className="btn btn-xs btn-secondary">
            Collection Link
          </a>
        )}
        {isBulkFile && (
          <button className="btn btn-xs btn-secondary" onClick={() => downloadInventoryFile(deal.id)}>
            Download Bulk File
          </button>
        )}
      </div>
    )
  }

  // Full-width detail row holding the wide tool panels (images, pricing log).
  // Returns null when nothing is open for this deal.
  function renderDetailRow(deal) {
    const imagesOpen = expandedDealImages === deal.id
    const logOpen = pricingLogView[deal.id] === 'log'
    if (!imagesOpen && !logOpen) return null

    return (
      <tr className="deal-detail-row">
        <td colSpan="4">
          {imagesOpen && (
            <div className="deal-images-container">
              {loadingDealImages === deal.id ? (
                <div className="loading-spinner" />
              ) : dealImages[deal.id]?.length > 0 ? (
                <div className="jotform-images-grid">
                  {dealImages[deal.id].map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt={`Image ${idx + 1}`}
                      className="jotform-image"
                      onClick={() => openLightbox(dealImages[deal.id], idx)}
                    />
                  ))}
                </div>
              ) : (
                <span className="text-muted">No images found</span>
              )}
            </div>
          )}
          {logOpen && (
            <div className="pricing-log-section" style={{ marginTop: imagesOpen ? 'var(--space-3)' : 0 }}>
              <div className="pricing-log-header">
                <strong>Pricing Log:</strong>
                {(() => {
                  const badge = getValidationBadge(deal.claude_pricing_log)
                  return badge ? <span className={badge.className}>{badge.label}</span> : null
                })()}
                <div style={{ display: 'flex', gap: '4px' }}>
                  {editingPricingLog === deal.id ? (
                    <>
                      <button className="btn btn-xs btn-primary" onClick={() => savePricingLog(deal.id)} disabled={savingPricingLog}>
                        {savingPricingLog ? 'Saving...' : 'Save'}
                      </button>
                      <button className="btn btn-xs btn-secondary" onClick={() => { setEditingPricingLog(null); setPricingLogValue('') }} disabled={savingPricingLog}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-xs btn-secondary" onClick={() => { setEditingPricingLog(deal.id); setPricingLogValue(deal.claude_pricing_log || '') }}>
                        Edit
                      </button>
                      {deal.claude_pricing_log && (
                        <button className="btn btn-xs btn-secondary" onClick={() => { navigator.clipboard.writeText(deal.claude_pricing_log); alert('Pricing log copied to clipboard!') }}>
                          Copy
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              {editingPricingLog === deal.id ? (
                <textarea
                  className="form-input"
                  rows={10}
                  value={pricingLogValue}
                  onChange={e => setPricingLogValue(e.target.value)}
                  style={{ fontFamily: 'monospace', fontSize: '0.8rem', width: '100%' }}
                  autoFocus
                />
              ) : (
                <div className="pricing-log">
                  {parsePricingLogSections(deal.claude_pricing_log).map((sec, idx) => (
                    <div key={idx} className="pricing-log-block">
                      <div className="pricing-log-block-header">{sec.title}</div>
                      <pre className="pricing-log-block-content">{sec.content}</pre>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </td>
      </tr>
    )
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <>
      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{deals.length}</div>
          <div className="stat-label">Total Deals</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--color-success)' }}>{deals.filter(d => d.claude_pricing_log).length}</div>
          <div className="stat-label">With Pricing Log</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--color-warning)' }}>{deals.filter(d => isAwaitingPricing(d) && !pricedButStale(d)).length}</div>
          <div className="stat-label">Awaiting Pricing</div>
        </div>
      </div>

      {/* Search */}
      <div className="card" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)' }}>
        <input
          type="text"
          className="form-input"
          placeholder="Search deals by name, items, or email..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Deals Table */}
      <div className="card" style={{ overflowX: 'auto' }}>
        <table className="table deals-table">
          <thead>
            <tr>
              <th>Deal Name</th>
              <th>Parsed Items</th>
              <th>Customer</th>
              <th>Tools</th>
            </tr>
          </thead>
          <tbody>
            {filteredDeals.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                  {searchTerm ? 'No deals match your search' : 'No deals in Quote Requested stage'}
                </td>
              </tr>
            ) : (
              filteredDeals.map(deal => (
                <React.Fragment key={deal.id}>
                  <tr>
                    <td className="title-cell">
                      <div className="deal-name">{deal.dealname || 'Unnamed Deal'}</div>
                      {(() => {
                        const conf = parsePricingLog(deal.claude_pricing_log)?.confidence
                        if (!conf || (!conf.ic && !conf.mc && !conf.fc)) return null
                        const fmt = (v) => (v == null ? '—' : v.toFixed(2))
                        const fcColor = !conf.fc ? 'var(--color-text-muted)'
                          : conf.fc.level === 'AUTOMATED' ? '#166534'
                          : conf.fc.level === 'AUTOMATED_WARNING' ? '#92400e'
                          : conf.fc.level === 'HIGH_RISK' ? '#9a3412'
                          : '#991b1b'
                        return (
                          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: 2 }}>
                            IC {fmt(conf.ic?.total)} · MC {fmt(conf.mc?.total)} ·{' '}
                            <span style={{ color: fcColor, fontWeight: 600 }}>FC {fmt(conf.fc?.total)}</span>
                          </div>
                        )
                      })()}
                      {deal.createdate && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: 2 }}>
                          Requested: {new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(deal.createdate))}
                        </div>
                      )}
                      {(() => {
                        const b = pricingStatusBadge(deal)
                        return b ? <span className={`badge ${b.cls} pricing-status-badge`}>{b.label}</span> : null
                      })()}
                    </td>
                    <td className="parsed-items-cell">
                      {renderParsedItems(deal)}
                    </td>
                    <td className="customer-cell">
                      {deal.seller_email ? (
                        <div className="customer-info">
                          <button
                            className={`customer-email-btn ${expandedDeal === deal.id ? 'active' : ''}`}
                            onClick={() => toggleExpanded(deal.id, deal.seller_email)}
                          >
                            {deal.seller_email}
                            <span className="expand-arrow">{expandedDeal === deal.id ? '▲' : '▼'}</span>
                          </button>
                          {(() => {
                            const count = deal.customer_deal_count
                            if (!count) return null
                            if (count <= 1) {
                              return <span className="badge customer-type-badge customer-type-new">✨ New customer</span>
                            }
                            const prev = deal.previous_quote_date
                            const when = prev
                              ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(prev))
                              : null
                            return (
                              <span className="badge customer-type-badge customer-type-return">
                                ↺ Return{when ? ` · last quote ${when}` : ''}
                              </span>
                            )
                          })()}
                          {expandedDeal === deal.id && (
                            <div className="customer-deals-dropdown">
                              <div className="dropdown-section-header">DEALS</div>
                              {loadingCustomerDeals[deal.id] ? (
                                <div style={{ padding: 16, textAlign: 'center' }}><div className="loading-spinner" /></div>
                              ) : customerDeals[deal.id]?.length > 0 ? (
                                <div className="customer-deals-list">
                                  {customerDeals[deal.id].map(cd => {
                                    const isCurrentDeal = cd.id === deal.id
                                    const stageClass = getStageClass(cd.dealstage)
                                    return (
                                      <div
                                        key={cd.id}
                                        className={`other-deal-item ${isCurrentDeal ? 'current' : 'clickable'}`}
                                        onClick={() => !isCurrentDeal && setSelectedOtherDeal({ ...cd, parentDealId: deal.id })}
                                      >
                                        <div className="other-deal-header">
                                          <span className={`badge badge-sm ${stageClass}`}>{getStageName(cd.dealstage)}</span>
                                          <span className="other-deal-date">{formatDate(cd.createdate)}</span>
                                          <span className="other-deal-amount">{formatCurrency(cd.amount)}</span>
                                        </div>
                                        <div className="other-deal-items">
                                          {cd.parsed_items?.split('\n').slice(0, 2).map((item, i) => (
                                            <div key={i} className="other-deal-item-line">{item}</div>
                                          ))}
                                          {cd.parsed_items?.split('\n').length > 2 && (
                                            <div className="other-deal-item-more">+{cd.parsed_items.split('\n').length - 2} more</div>
                                          )}
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                              ) : (
                                <div className="text-muted" style={{ padding: 12 }}>No deals found</div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted">No email</span>
                      )}
                    </td>
                    <td className="tools-cell">
                      {renderTools(deal)}
                    </td>
                  </tr>
                  {renderDetailRow(deal)}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Lightbox */}
      {lightbox.open && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>&times;</button>
            <button
              className="btn btn-sm btn-secondary"
              onClick={downloadCurrentImage}
              style={{ position: 'absolute', top: 12, left: 12, zIndex: 2 }}
            >
              Download
            </button>

            {lightbox.images.length > 1 && (
              <button className="lightbox-nav lightbox-prev" onClick={prevImage}>&#8249;</button>
            )}

            <img
              src={lightbox.images[lightbox.currentIndex]}
              alt={`Image ${lightbox.currentIndex + 1}`}
              className="lightbox-image"
            />

            {lightbox.images.length > 1 && (
              <button className="lightbox-nav lightbox-next" onClick={nextImage}>&#8250;</button>
            )}

            <div className="lightbox-counter">
              {lightbox.currentIndex + 1} / {lightbox.images.length}
            </div>

            {lightbox.images.length > 1 && (
              <div className="lightbox-thumbnails">
                {lightbox.images.map((url, idx) => (
                  <img
                    key={idx}
                    src={url}
                    alt={`Thumbnail ${idx + 1}`}
                    className={`lightbox-thumb ${idx === lightbox.currentIndex ? 'active' : ''}`}
                    onClick={() => setLightbox(prev => ({ ...prev, currentIndex: idx }))}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Other Deal Details Popup */}
      {selectedOtherDeal && (
        <div className="modal-overlay">
          <div className="modal-content modal-deal-detail" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Deal Details</h2>
              <button className="modal-close" onClick={() => setSelectedOtherDeal(null)}>&times;</button>
            </div>
            <div className="modal-body-scroll">
              {/* Deal name */}
              <div className="popup-dealname">{selectedOtherDeal.dealname || 'Unnamed Deal'}</div>
              <div className="popup-deal-id"><DealLink deal={selectedOtherDeal} /></div>

              {/* Date at top */}
              <div className="popup-date-row">
                <span className="popup-date">{formatDate(selectedOtherDeal.createdate)}</span>
                <span className="popup-amount">{formatCurrency(selectedOtherDeal.amount)}</span>
              </div>

              {/* Stage with dropdown */}
              <div className="popup-stage-row">
                <span className={`badge ${getStageClass(selectedOtherDeal.dealstage)}`}>
                  {getStageName(selectedOtherDeal.dealstage)}
                </span>
                <select
                  className="form-input form-input-sm"
                  value={selectedOtherDeal.dealstage || ''}
                  onChange={e => updateOtherDealStage(selectedOtherDeal.id, e.target.value, selectedOtherDeal.parentDealId)}
                  disabled={updatingOtherDealStage}
                  style={{ flex: 1, marginLeft: 12 }}
                >
                  {DEAL_STAGES.filter(stage => stage.id).map(stage => (
                    <option key={stage.id} value={stage.id}>{stage.label}</option>
                  ))}
                </select>
              </div>

              {/* Parsed items - each showing name and BP */}
              <div className="popup-items-section">
                <div className="deal-detail-label">ITEMS</div>
                {selectedOtherDeal.parsed_items && selectedOtherDeal.parsed_items.trim() ? (
                  <div className="popup-items-list">
                    {selectedOtherDeal.parsed_items.split('\n').filter(line => line.trim()).map((item, i) => {
                      const parsed = parseItemLine(item)
                      return (
                        <div key={i} className="popup-item-row">
                          <span className="popup-item-num">{i + 1}.</span>
                          <span className="popup-item-name">{parsed.description || item}</span>
                          <span className="popup-item-bp">BP: £{parsed.bp || '0'}</span>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-muted" style={{ padding: '8px 0' }}>No parsed items - Deal name: {selectedOtherDeal.dealname}</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-reject"
                onClick={() => markOtherDealAsDuplicate(selectedOtherDeal.id, selectedOtherDeal.parentDealId)}
                disabled={markingOtherDuplicate}
              >
                {markingOtherDuplicate ? 'Marking...' : 'Mark as Duplicate'}
              </button>
              <button className="btn btn-secondary" onClick={() => setSelectedOtherDeal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {addItemDeal && (
        <AddItemDialog busy={savingItem} onCancel={() => setAddItemDeal(null)} onConfirm={addItem} />
      )}
      {removeAsk && (
        <ConfirmActionDialog
          title={`Remove item ${removeAsk.position}?`}
          message={removeAsk.name}
          confirmLabel="Remove"
          danger
          busy={savingItem}
          onCancel={() => setRemoveAsk(null)}
          onConfirm={removeItem}
        />
      )}

      {repriceToast && (
        <div className={`feedback-toast feedback-toast-${repriceToast.type}`}>
          {repriceToast.message}
        </div>
      )}
    </>
  )
}

// Get condition badge class
function getConditionClass(condition) {
  const c = (condition || '').toLowerCase()
  if (c === 'mint') return 'condition-new'
  if (c === 'excellent') return 'condition-excellent'
  if (c === 'very good') return 'condition-very-good'
  if (c === 'good') return 'condition-good'
  if (c === 'fair') return 'condition-fair'
  if (c === 'poor') return 'condition-poor'
  if (c === 'broken') return 'condition-not-working'
  return 'badge-neutral'
}

// Get stage badge class based on stage name
function getStageClass(stageId) {
  // Quote stages - teal/green
  if (['4383256790', '24798453', '65923846', 'appointmentscheduled'].includes(stageId)) {
    return 'stage-quote-sent'
  }
  // Quote Requested - orange/yellow
  if (['112025330', '24798454'].includes(stageId)) {
    return 'stage-quote-requested'
  }
  // Completed stages - green
  if (['closedwon', '126498898', '24798455'].includes(stageId)) {
    return 'stage-completed'
  }
  // Lost/rejected stages - red
  if (['closedlost', '24798456', '1616888039'].includes(stageId)) {
    return 'stage-lost'
  }
  return 'badge-neutral'
}

export default QuoteRequested
